/* Emoji icon picker for goals. Shows a button with the current emoji that opens a popover grid. */

import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface GoalIconPickerProps {
  value: string;
  onChange: (emoji: string) => void;
}

const EMOJI_CATEGORIES = [
  {
    name: "Productivity",
    emojis: [
      "\u{1F3AF}", "\u2705", "\u{1F4DD}", "\u{1F4CA}", "\u{1F4C5}", "\u23F0", "\u{1F3C6}", "\u2B50",
      "\u{1F680}", "\u{1F4A1}", "\u{1F525}", "\u26A1", "\u{1F396}\uFE0F", "\u{1F4CC}",
    ],
  },
  {
    name: "Fitness & Health",
    emojis: [
      "\u{1F4AA}", "\u{1F3C3}", "\u{1F3CB}\uFE0F", "\u{1F9D8}", "\u{1F6B4}", "\u{1F3CA}", "\u26BD", "\u{1F3C0}",
      "\u{1F3BE}", "\u{1F94A}", "\u{1F9D7}", "\u2764\uFE0F", "\u{1F9E0}", "\u{1F634}", "\u{1F48A}",
    ],
  },
  {
    name: "Learning",
    emojis: [
      "\u{1F4DA}", "\u{1F4D6}", "\u{1F393}", "\u270D\uFE0F", "\u{1F52C}", "\u{1F9EA}", "\u{1F5E3}\uFE0F", "\u{1F310}",
      "\u{1F4D0}", "\u{1F9EE}", "\u{1F4BB}", "\u{1F9D1}\u200D\u{1F4BB}",
    ],
  },
  {
    name: "Creative",
    emojis: [
      "\u{1F3A8}", "\u{1F3B5}", "\u{1F3AC}", "\u{1F4F8}", "\u{1F3B8}", "\u{1F3B9}", "\u{1F3A4}", "\u270F\uFE0F",
      "\u{1F58C}\uFE0F", "\u{1F3AD}", "\u{1F4FD}\uFE0F", "\u{1F3B6}",
    ],
  },
  {
    name: "Nature & Wellness",
    emojis: [
      "\u{1F331}", "\u{1F33F}", "\u2600\uFE0F", "\u{1F319}", "\u{1F30A}", "\u{1F343}", "\u{1F338}", "\u{1F333}",
      "\u{1F98B}", "\u{1F415}", "\u{1F3D4}\uFE0F",
    ],
  },
  {
    name: "Lifestyle",
    emojis: [
      "\u2615", "\u{1F34E}", "\u{1F373}", "\u{1F957}", "\u{1F9F9}", "\u2708\uFE0F", "\u{1F3E0}", "\u{1F4B0}",
      "\u{1F6D2}", "\u{1F468}\u200D\u{1F469}\u200D\u{1F467}\u200D\u{1F466}", "\u{1F3AE}", "\u{1F30D}",
    ],
  },
] as const;

export function GoalIconPicker({ value, onChange }: GoalIconPickerProps) {
  const [open, setOpen] = useState(false);
  const [customInput, setCustomInput] = useState("");

  function handleEmojiClick(emoji: string) {
    onChange(emoji);
    setOpen(false);
  }

  function handleCustomInput(input: string) {
    setCustomInput(input);
    // Extract first emoji character from the input
    const emojiMatch = input.match(
      /\p{Emoji_Presentation}|\p{Emoji}\uFE0F/u,
    );
    if (emojiMatch) {
      onChange(emojiMatch[0]);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className="size-10 text-xl p-0 shrink-0"
        >
          {value}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-3">
          <Input
            placeholder="Type or paste emoji..."
            value={customInput}
            onChange={(e) => handleCustomInput(e.target.value)}
            className="text-center text-lg"
            autoComplete="off"
          />
          <div className="max-h-60 overflow-y-auto space-y-2.5">
            {EMOJI_CATEGORIES.map((category) => (
              <div key={category.name}>
                <p className="text-[11px] font-medium text-muted-foreground mb-1">
                  {category.name}
                </p>
                <div className="grid grid-cols-7 gap-0.5">
                  {category.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                      className={`size-8 rounded-md text-lg flex items-center justify-center hover:bg-accent transition-colors ${
                        value === emoji
                          ? "bg-primary/10 ring-1.5 ring-primary"
                          : ""
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
